import React, { useState } from 'react'
import { useCredentialSets } from '../context/CredentialSetContext'
import { AccountType, IGCredentialSetCreate, IGCredentialSetUpdate } from '../types'
import { X, Plus, Trash2, Edit2, Check, AlertCircle, Star } from 'lucide-react'

interface CredentialSetManagerProps {
  onClose: () => void
}

export const CredentialSetManager: React.FC<CredentialSetManagerProps> = ({ onClose }) => {
  const {
    credentialSets,
    preferredCredentialSet,
    loading,
    error: contextError,
    createCredentialSet,
    updateCredentialSet,
    deleteCredentialSet,
    setPreferredCredentialSet,
    refreshCredentialSets,
  } = useCredentialSets()

  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [formData, setFormData] = useState<IGCredentialSetCreate>({
    nickname: '',
    ig_api_key: '',
    ig_username: '',
    ig_password: '',
    ig_account_type: 'DEMO',
  })
  const [updateData, setUpdateData] = useState<IGCredentialSetUpdate>({
    nickname: '',
    ig_api_key: '',
    ig_username: '',
    ig_password: '',
  })
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const handleAddCredentialSet = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormLoading(true)
    setFormError(null)

    try {
      await createCredentialSet(formData)
      setIsAdding(false)
      setFormData({
        nickname: '',
        ig_api_key: '',
        ig_username: '',
        ig_password: '',
        ig_account_type: 'DEMO',
      })
      await refreshCredentialSets()
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to add credential set')
    } finally {
      setFormLoading(false)
    }
  }

  const handleUpdateCredentialSet = async (id: number, e: React.FormEvent) => {
    e.preventDefault()
    setFormLoading(true)
    setFormError(null)

    try {
      // Only send fields that have values
      const dataToUpdate: IGCredentialSetUpdate = {}
      if (updateData.nickname) dataToUpdate.nickname = updateData.nickname
      if (updateData.ig_api_key) dataToUpdate.ig_api_key = updateData.ig_api_key
      if (updateData.ig_username) dataToUpdate.ig_username = updateData.ig_username
      if (updateData.ig_password) dataToUpdate.ig_password = updateData.ig_password

      await updateCredentialSet(id, dataToUpdate)
      setEditingId(null)
      setUpdateData({
        nickname: '',
        ig_api_key: '',
        ig_username: '',
        ig_password: '',
      })
      await refreshCredentialSets()
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to update credential set')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDeleteCredentialSet = async (id: number, nickname: string) => {
    if (confirm(`Are you sure you want to delete "${nickname}"?`)) {
      try {
        await deleteCredentialSet(id)
        await refreshCredentialSets()
      } catch (err: any) {
        alert(err.response?.data?.detail || 'Failed to delete credential set')
      }
    }
  }

  const handleSetPreferred = async (id: number) => {
    try {
      await setPreferredCredentialSet(id)
      await refreshCredentialSets()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to set preferred credential set')
    }
  }

  const startEditing = (id: number, nickname: string) => {
    setEditingId(id)
    setUpdateData({
      nickname: nickname,
      ig_api_key: '',
      ig_username: '',
      ig_password: '',
    })
    setFormError(null)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-dark-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-dark-600">
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <h2 className="text-2xl font-bold text-arcade-text">IG Credential Sets</h2>
          <button
            onClick={onClose}
            className="text-arcade-dim hover:text-neon-pink transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {contextError && (
            <div className="mb-4 p-3 bg-neon-pink/10 border border-neon-pink/30 rounded-lg text-neon-pink text-sm">
              {contextError}
            </div>
          )}

          {loading && !formLoading ? (
            <div className="text-center py-8 text-arcade-dim">Loading credential sets...</div>
          ) : (
            <>
              {/* Existing Credential Sets */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 text-arcade-text">Your Credential Sets</h3>
                {credentialSets.length === 0 ? (
                  <p className="text-arcade-dim text-sm">
                    No credential sets configured yet. Add one below to get started.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {credentialSets.map((credSet) => (
                      <div key={credSet.id} className="border border-dark-600 rounded-lg bg-dark-700">
                        <div className="flex items-center justify-between p-4 hover:bg-dark-600/50">
                          <div className="flex items-center gap-3 flex-1">
                            {preferredCredentialSet?.id === credSet.id && (
                              <Star size={18} className="text-neon-orange fill-neon-orange" />
                            )}
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                credSet.ig_account_type === 'DEMO'
                                  ? 'bg-neon-blue/20 text-neon-blue'
                                  : 'bg-neon-green/20 text-neon-green'
                              }`}
                            >
                              {credSet.ig_account_type}
                            </span>
                            <span className="font-medium text-arcade-text">{credSet.nickname}</span>
                            <span className="text-xs text-arcade-dim">
                              (ID: {credSet.id})
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {preferredCredentialSet?.id !== credSet.id && (
                              <button
                                onClick={() => handleSetPreferred(credSet.id)}
                                className="text-arcade-dim hover:text-neon-orange text-sm px-3 py-1 border border-dark-600 rounded hover:border-neon-orange hover:bg-neon-orange/10"
                                title="Set as preferred"
                              >
                                <Star size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => startEditing(credSet.id, credSet.nickname)}
                              className="text-neon-blue hover:text-neon-blue/80 text-sm px-3 py-1 border border-neon-blue rounded hover:bg-neon-blue/10"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCredentialSet(credSet.id, credSet.nickname)}
                              className="text-neon-pink hover:text-neon-pink/80 p-2"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>

                        {/* Edit Credential Set Form */}
                        {editingId === credSet.id && (
                          <div className="border-t border-dark-600 p-4 bg-dark-800">
                            <h4 className="font-medium mb-3 text-arcade-text">Update Credential Set</h4>
                            <form onSubmit={(e) => handleUpdateCredentialSet(credSet.id, e)} className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-arcade-muted mb-1">
                                  Nickname
                                </label>
                                <input
                                  type="text"
                                  value={updateData.nickname || ''}
                                  onChange={(e) =>
                                    setUpdateData({ ...updateData, nickname: e.target.value })
                                  }
                                  className="w-full border border-dark-600 bg-dark-700 rounded-lg px-3 py-2 text-sm text-arcade-text placeholder-arcade-dim focus:ring-neon-blue focus:border-neon-blue"
                                  placeholder={credSet.nickname}
                                />
                                <p className="text-xs text-arcade-dim mt-1">Leave blank to keep current</p>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-arcade-muted mb-1">
                                  IG API Key
                                </label>
                                <input
                                  type="text"
                                  value={updateData.ig_api_key || ''}
                                  onChange={(e) =>
                                    setUpdateData({ ...updateData, ig_api_key: e.target.value })
                                  }
                                  className="w-full border border-dark-600 bg-dark-700 rounded-lg px-3 py-2 text-sm text-arcade-text placeholder-arcade-dim focus:ring-neon-blue focus:border-neon-blue"
                                  placeholder="Leave blank to keep current"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-arcade-muted mb-1">
                                  IG Username
                                </label>
                                <input
                                  type="text"
                                  value={updateData.ig_username || ''}
                                  onChange={(e) =>
                                    setUpdateData({ ...updateData, ig_username: e.target.value })
                                  }
                                  className="w-full border border-dark-600 bg-dark-700 rounded-lg px-3 py-2 text-sm text-arcade-text placeholder-arcade-dim focus:ring-neon-blue focus:border-neon-blue"
                                  placeholder="Leave blank to keep current"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-arcade-muted mb-1">
                                  IG Password
                                </label>
                                <input
                                  type="password"
                                  value={updateData.ig_password || ''}
                                  onChange={(e) =>
                                    setUpdateData({ ...updateData, ig_password: e.target.value })
                                  }
                                  className="w-full border border-dark-600 bg-dark-700 rounded-lg px-3 py-2 text-sm text-arcade-text placeholder-arcade-dim focus:ring-neon-blue focus:border-neon-blue"
                                  placeholder="Leave blank to keep current"
                                />
                              </div>

                              {formError && (
                                <div className="text-neon-pink text-sm bg-neon-pink/10 p-2 rounded border border-neon-pink/30">
                                  {formError}
                                </div>
                              )}

                              <div className="flex gap-2">
                                <button
                                  type="submit"
                                  disabled={formLoading}
                                  className="bg-neon-blue text-dark-900 px-4 py-2 rounded-lg hover:bg-neon-blue/80 hover:shadow-[0_0_15px_rgba(0,212,255,0.5)] disabled:opacity-50 text-sm font-semibold transition-all"
                                >
                                  {formLoading ? 'Updating...' : 'Update'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingId(null)
                                    setFormError(null)
                                    setUpdateData({
                                      nickname: '',
                                      ig_api_key: '',
                                      ig_username: '',
                                      ig_password: '',
                                    })
                                  }}
                                  className="px-4 py-2 border border-dark-600 rounded-lg hover:bg-dark-700 text-sm text-arcade-muted"
                                >
                                  Cancel
                                </button>
                              </div>
                            </form>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add New Credential Set */}
              {!isAdding ? (
                <button
                  onClick={() => setIsAdding(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-neon-blue text-dark-900 rounded-lg hover:bg-neon-blue/80 hover:shadow-[0_0_15px_rgba(0,212,255,0.5)] transition-all font-semibold"
                >
                  <Plus size={20} />
                  Add New Credential Set
                </button>
              ) : (
                <div className="border border-dark-600 rounded-lg p-4 bg-dark-700">
                  <h3 className="text-lg font-semibold mb-4 text-arcade-text">Add New Credential Set</h3>
                  <form onSubmit={handleAddCredentialSet} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-arcade-muted mb-1">
                        Nickname *
                      </label>
                      <input
                        type="text"
                        value={formData.nickname}
                        onChange={(e) =>
                          setFormData({ ...formData, nickname: e.target.value })
                        }
                        className="w-full border border-dark-600 bg-dark-800 rounded-lg px-3 py-2 text-sm text-arcade-text placeholder-arcade-dim focus:ring-neon-blue focus:border-neon-blue"
                        placeholder="e.g., Demo Account, Prod - Main"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-arcade-muted mb-1">
                        Account Type *
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            value="DEMO"
                            checked={formData.ig_account_type === 'DEMO'}
                            onChange={(e) =>
                              setFormData({ ...formData, ig_account_type: e.target.value as AccountType })
                            }
                            className="w-4 h-4 text-neon-blue focus:ring-neon-blue"
                          />
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-neon-blue/20 text-neon-blue">
                            DEMO
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            value="PROD"
                            checked={formData.ig_account_type === 'PROD'}
                            onChange={(e) =>
                              setFormData({ ...formData, ig_account_type: e.target.value as AccountType })
                            }
                            className="w-4 h-4 text-neon-green focus:ring-neon-green"
                          />
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-neon-green/20 text-neon-green">
                            PROD
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="border-t border-dark-600 pt-4 mt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertCircle size={18} className="text-neon-blue" />
                        <h4 className="font-medium text-arcade-text">IG Trading API Credentials</h4>
                      </div>
                      <p className="text-xs text-arcade-dim mb-4">
                        These credentials are encrypted and stored securely in the database.
                      </p>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-arcade-muted mb-1">
                            IG API Key *
                          </label>
                          <input
                            type="text"
                            value={formData.ig_api_key}
                            onChange={(e) =>
                              setFormData({ ...formData, ig_api_key: e.target.value })
                            }
                            className="w-full border border-dark-600 bg-dark-800 rounded-lg px-3 py-2 text-sm text-arcade-text placeholder-arcade-dim focus:ring-neon-blue focus:border-neon-blue"
                            placeholder="Your IG API key"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-arcade-muted mb-1">
                            IG Username *
                          </label>
                          <input
                            type="text"
                            value={formData.ig_username}
                            onChange={(e) =>
                              setFormData({ ...formData, ig_username: e.target.value })
                            }
                            className="w-full border border-dark-600 bg-dark-800 rounded-lg px-3 py-2 text-sm text-arcade-text placeholder-arcade-dim focus:ring-neon-blue focus:border-neon-blue"
                            placeholder="Your IG username"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-arcade-muted mb-1">
                            IG Password *
                          </label>
                          <input
                            type="password"
                            value={formData.ig_password}
                            onChange={(e) =>
                              setFormData({ ...formData, ig_password: e.target.value })
                            }
                            className="w-full border border-dark-600 bg-dark-800 rounded-lg px-3 py-2 text-sm text-arcade-text placeholder-arcade-dim focus:ring-neon-blue focus:border-neon-blue"
                            placeholder="Your IG password"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {formError && (
                      <div className="text-neon-pink text-sm bg-neon-pink/10 p-3 rounded border border-neon-pink/30">
                        {formError}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={formLoading}
                        className="flex-1 bg-neon-blue text-dark-900 py-2 rounded-lg hover:bg-neon-blue/80 hover:shadow-[0_0_15px_rgba(0,212,255,0.5)] disabled:opacity-50 transition-all font-semibold"
                      >
                        {formLoading ? 'Adding...' : 'Add Credential Set'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsAdding(false)
                          setFormError(null)
                          setFormData({
                            nickname: '',
                            ig_api_key: '',
                            ig_username: '',
                            ig_password: '',
                            ig_account_type: 'DEMO',
                          })
                        }}
                        className="px-4 py-2 border border-dark-600 rounded-lg hover:bg-dark-800 transition-colors text-arcade-muted"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
